/**
 * Onboarding Service
 *
 * Simplified after refactoring — wizard step state is now held in client-side
 * React state only (no Table Storage session). SharePoint WVHealth_ToolConfig
 * is the single source of truth for SetupComplete.
 *
 * Only two operations remain:
 *   - completeOnboarding()  → marks SetupComplete=true in SharePoint (upsert)
 *   - getLicensedSites()    → returns allowedSiteUrls from the license
 */

import { getSPGraphAccessToken, getSPRestAccessToken } from './authService';
import { getSharePointRootHostname } from './sharePointService';
import { verifyCurrentTenantLicense } from './licenseService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// ─── Shared Types ────────────────────────────────────────────────────────────

/** A SharePoint site entry used during the ConnectSharePoint wizard step */
export interface SiteEntry {
    url: string;
    status: 'validating' | 'provisioning' | 'ready' | 'error';
    isPrimary: boolean;
    siteName?: string;
    error?: string;
}

// ─── License Guard ───────────────────────────────────────────────────────────

async function ensureActiveLicense(): Promise<void> {
    const license = await verifyCurrentTenantLicense();
    if (!license || license.status === 'Invalid') {
        throw new Error('License validation failed. Cannot proceed with tenant-scoped operations.');
    }
    if (license.status === 'Expired') {
        throw new Error('Your license has expired. Please contact your administrator to renew.');
    }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Mark onboarding as completed.
 * Upserts SetupComplete=true into the SharePoint WVHealth_ToolConfig list.
 * If the TENANT_CONFIG item was deleted it will be recreated automatically.
 */
export async function completeOnboarding(
    primarySiteUrl: string
): Promise<{ onboardingCompleted: boolean; onboardingCompletedAt: string }> {
    await ensureActiveLicense();

    // Bearer token for /api/onboarding-complete is the SP Graph token —
    // the backend uses it to write directly to SharePoint.
    const token = await getSPGraphAccessToken();

    // Also request the SharePoint REST scope here, while this button click gives
    // us a genuine user gesture to anchor the interactive consent popup to. This
    // is the one-time consent grant that checkLoginAccess() later relies on
    // acquiring silently on every subsequent login (see bootstrapService.ts).
    // Best-effort — onboarding must not fail just because this prompt was
    // skipped, blocked, or requires admin consent not yet granted.
    try {
        const hostname = await getSharePointRootHostname(token);
        if (hostname) await getSPRestAccessToken(hostname);
    } catch (error) {
        console.warn('[onboardingService] Could not pre-consent SP REST scope:', error);
    }

    const response = await fetch(`${API_BASE_URL}/onboarding-complete`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ primarySiteUrl }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || error.details || 'Failed to complete onboarding');
    }

    return response.json();
}

/**
 * Return the list of SharePoint site URLs allowed by the current tenant license.
 * The first entry is the primary site URL.
 */
export async function getLicensedSites(): Promise<string[]> {
    const license = await verifyCurrentTenantLicense();
    if (!license || license.status === 'Invalid') return [];
    return license.allowedSiteUrls ?? [];
}
